import React, { useEffect, useState } from 'react'

import { Notification, CTABanner, Modal, ModalCue, Message, Label } from '@pb/components'
import { getLabelFromStorage, Breakpoints, getLocale } from '@pb/utils'
import { useDecision } from '@optimizely/react-sdk'
import cn from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'

import { ACCOUNT_ROUTES } from 'App/constants'
import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'
import clubCardSelectors from 'features/account/club-card/duck/selectors'
import { ACCOUNT_TRACKING_EVENTS } from 'features/account/constants'
import DonateVoucherModal from 'features/account/donate-voucher/components/DonateVoucherModal'
import { PAGE_IDS } from 'features/account/helpers/getAccountTrackingData'
import useAccountFeature from 'features/account/hooks/useAccountFeature'
import useOptimizelyDecision, {
  useNorthstarABTestEnabled,
} from 'features/account/hooks/useOptimizelyDecision'
import useTrackAccountClickEvent from 'features/account/hooks/useTrackAccountClickEvent'
import useTrackAccountPageView from 'features/account/hooks/useTrackAccountPageView'
import { InboxModal, inboxSelectors } from 'features/account/inbox'
import { operationFetchNotifications } from 'features/account/inbox/duck/operations'
import UpgradeToClub from 'features/account/upgrade-to-club'
import userMenuActions from 'features/account/user-menu/duck/actions'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import NewMemberCardNS from 'features/account/new-member-card/NewMemberCardNS'

import {
  operationFetchClubJoinBannerStart,
  operationSetJoinClubModalVisibility,
} from '../../duck/operations'
import {
  getShowJoinClubModal,
  getShouldShowPayLaterConfirmationNotification,
  getShouldShowClubJoinMessage,
  getShouldFetchClubJoinBanner,
  getShouldShowPayLaterActivationPage,
  getIsClubJoinBannerLoading,
  getShouldShowJoinedClubConfirmationNotification,
  getShouldShowPayLaterActivationBanner,
} from '../../duck/selectors'
import DesktopFailedUser from '../DesktopFailedUser'
import DesktopFullMember from '../DesktopFullMember'
import DesktopNonMember from '../DesktopNonMember'
import DesktopPendingMember from '../DesktopPendingMember'
import styles from '../Hub.scss'
import MobileFailedUser from '../MobileFailedUser'
import MobileFullMember from '../MobileFullMember'
import MobileNonMember from '../MobileNonMember'
import MobilePendingMember from '../MobilePendingMember'

const isLargeInitialState = null

const HubOverview: React.FC = () => {
  const [isLarge, setIsLarge] = useState<boolean | null>(isLargeInitialState)
  const [isFNBannerClosed, setFNBannerIsClosed] = useState<boolean | null>(false)
  const [isFNNoOfferBannerClosed, setFNNoOfferBannerIsClosed] = useState<boolean | null>(false)

  const dispatch = useDispatch()

  const trackAccountPageView = useTrackAccountPageView()
  const trackPDFClick = useTrackAccountClickEvent()
  const trackFNBannerClick = useTrackAccountClickEvent()

  const isUserLoading = useSelector(appSelectors.getIsUserLoading)
  const hasUserFailed = useSelector(appSelectors.getIsUserLoadingFailed)

  const userIsNonMember = useSelector(appSelectors.getIsUserOldAccountHolder)
  const userIsFullMember = useSelector(appSelectors.getUserIsFullMember)
  const userIsPendingMember = useSelector(appSelectors.getUserIsPendingMember)
  const userDisplayName = useSelector(appSelectors.getUserDisplayName)
  const userEmail = useSelector(appSelectors.getUserEmail)

  const shouldShowClubJoinMessage = useSelector(getShouldShowClubJoinMessage)
  const shouldShowClubCard = useSelector(clubCardSelectors.getShouldShowClubCard)
  const shouldFetchClubJoinBanner = useSelector(getShouldFetchClubJoinBanner)
  const shouldShowPayLaterOptin = useSelector(getShouldShowPayLaterActivationPage)
  const shouldShowPayLaterActivationBanner = useSelector(getShouldShowPayLaterActivationBanner)

  const shouldShowPayLaterConfirmationNotification = useSelector(
    getShouldShowPayLaterConfirmationNotification,
  )
  const shouldShowJoinedClubConfirmationNotification = useSelector(
    getShouldShowJoinedClubConfirmationNotification,
  )
  const shouldFetchMessages = useSelector(inboxSelectors.getShouldFetchMessages)

  const screenSize = useSelector(appSelectors.getScreenSizeState)
  const isClubJoinBannerLoading = useSelector(getIsClubJoinBannerLoading)

  const fetchClubJoinBanner = (): any => dispatch(operationFetchClubJoinBannerStart())
  const fetchNotifications = (): any => dispatch(operationFetchNotifications())
  const setJoinClubModalVisibility = (isVisible): any =>
    dispatch(operationSetJoinClubModalVisibility(isVisible))

  const inviteFriendPageEnabled = useSelector(appSelectors.getIsInviteFriendEnabled)
  const showJoinClubModal = useSelector(getShowJoinClubModal)

  const history = useHistory()

  const [{ enabled: isFNBAnnerCampaignActive }] = useDecision(
    OPTIMIZELY.LCA_FASHION_NEWS_CAMPAIGN_BANNER,
  )

  const [{ enabled: isFNBAnnerNoOfferCampaignActive }] = useDecision(
    OPTIMIZELY.LCA_FASHION_NEWS_NO_OFFER_CAMPAIGN_BANNER,
  )

  const userIsSubscribedToFashionNews = useSelector(appSelectors.getIsUserSubscribedToFashionNews)

  const [{ variables }] = useDecision(OPTIMIZELY.LCA_FASHION_NEWS_NO_OFFER_CAMPAIGN_BANNER)
  const isNorthstarBrand = useOptimizelyDecision(OPTIMIZELY.ACCOUNT_NORTHSTAR_VARIATION)
  const isNorthStarABtestEnabled = useNorthstarABTestEnabled(OPTIMIZELY.ACCOUNT_NORTHSTAR_VARIATION)

  const variationLocales = variables && (variables.markets_message as Object)

  const {
    pages: {
      hub: { shouldShowMemberBanner },
    },
  } = useAccountFeature()

  useEffect(() => {
    if (shouldShowPayLaterOptin) history.push(ACCOUNT_ROUTES.PayLaterStoreActivationInfo.path)
    trackAccountPageView(PAGE_IDS.account)
    if (isNorthStarABtestEnabled) trackOptimizelyEvent(OPTIMIZELY.ACCOUNT_LANDING_PAGE_VIEW)
  }, [])

  useEffect(() => {
    if (shouldFetchClubJoinBanner) {
      fetchClubJoinBanner()
    }
  }, [shouldFetchClubJoinBanner])

  useEffect(() => {
    if (shouldFetchMessages) {
      fetchNotifications()
    }
  }, [shouldFetchMessages])

  useEffect(() => {
    if (inviteFriendPageEnabled && userIsFullMember) {
      dispatch(userMenuActions.addInviteFriendMenuItem())
    }
  }, [inviteFriendPageEnabled])

  const pendingMemberTryAgainAction = (): any => {
    window.location.reload()
  }

  const handleActivationBannerPress = (): void => {
    trackPDFClick(ACCOUNT_TRACKING_EVENTS.onPayLaterActivationClick)()
    history.push(ACCOUNT_ROUTES.PayLaterStoreActivationInfo.path)
  }

  const onSubscribeClick = (): any => {
    if (isFNBAnnerNoOfferCampaignActive) {
      trackOptimizelyEvent(OPTIMIZELY.CLICK_FN_NO_OFFER_BANNER_CTA)
    }

    if (isFNBAnnerCampaignActive) {
      trackFNBannerClick(ACCOUNT_TRACKING_EVENTS.onFNBannerCTAClick)()
    }
    history.push(ACCOUNT_ROUTES.SettingsEditNewsletterSubscription.path)
  }

  const onMessageClose = (): void => {
    setTimeout(() => {
      setFNBannerIsClosed(true)
      setFNNoOfferBannerIsClosed(!isFNNoOfferBannerClosed)
    }, 1000)
  }

  const getLocaleNoOfferBannerText = (isHeader): string => {
    const locale = getLocale()

    if (Object.prototype.hasOwnProperty.call(variationLocales ?? {}, locale)) {
      return isHeader ? variationLocales[locale].header : variationLocales[locale].message
    }

    return ''
  }

  useEffect(() => {
    setIsLarge(screenSize === Breakpoints.XLARGE || screenSize === Breakpoints.XXL)

    if (!isFNBAnnerCampaignActive) {
      setFNBannerIsClosed(true)
    }
  }, [screenSize])

  const components = {
    DesktopFailedUser,
    MobileFailedUser,
    DesktopNonMember,
    MobileNonMember,
    DesktopPendingMember,
    MobilePendingMember,
    DesktopFullMember,
    MobileFullMember,
  }

  const device = isLarge ? 'Desktop' : 'Mobile'
  const component = hasUserFailed
    ? 'FailedUser'
    : userIsNonMember
    ? 'NonMember'
    : userIsPendingMember
    ? 'PendingMember'
    : userIsFullMember
    ? 'FullMember'
    : ''

  const RenderComponent = components[device + component]
  return (
    <div className={styles.bleed}>
      {shouldShowPayLaterConfirmationNotification && (
        <Notification type="success">
          <Label copyKey="omnicredit.optin.welcome.message" />
        </Notification>
      )}
      {shouldShowJoinedClubConfirmationNotification && (
        <Notification type="success">
          <Label copyKey="hub.membership.confirmation" />
        </Notification>
      )}
      {shouldShowClubJoinMessage && !isClubJoinBannerLoading && shouldShowMemberBanner && (
        <CTABanner
          heading={<Label copyKey="hub.become.member.title" />}
          text={<Label copyKey="hub.join.now.offer.text" />}
          onClick={() => setJoinClubModalVisibility(true)}
        />
      )}

      {!userIsSubscribedToFashionNews && isFNBAnnerNoOfferCampaignActive && (
        <Message
          heading={<Label copyKey={getLocaleNoOfferBannerText(true)} />}
          text={<Label copyKey={getLocaleNoOfferBannerText(false)} />}
          cta={{
            text: <Label copyKey="account.signup.fn.cta" />,
            tag: 'anchor',
            onClick: () => onSubscribeClick(),
          }}
          isClosable
          onMessageClose={() => onMessageClose()}
        />
      )}

      {!userIsSubscribedToFashionNews && isFNBAnnerCampaignActive && (
        <Message
          heading={<Label copyKey="account.signup.fn.title" />}
          text={<Label copyKey="account.signup.fn.message" />}
          cta={{
            text: <Label copyKey="account.signup.fn.cta" />,
            tag: 'anchor',
            onClick: () => onSubscribeClick(),
          }}
          isClosable
          onMessageClose={() => onMessageClose()}
        />
      )}

      {shouldShowPayLaterActivationBanner &&
        (isFNNoOfferBannerClosed || isFNBannerClosed || userIsSubscribedToFashionNews) && (
          <Message
            heading={<Label copyKey="account.signup.klarna.title" />}
            text={<Label copyKey="account.signup.klarna.message" />}
            cta={{
              text: <Label copyKey="account.signup.klarna.ok" />,
              tag: 'anchor',
              onClick: handleActivationBannerPress,
            }}
            isClosable
          />
        )}

      {shouldShowClubCard && isNorthstarBrand && RenderComponent === DesktopFullMember && (
        <NewMemberCardNS />
      )}
      <div className={cn(styles.container, !isNorthstarBrand && styles.containerWidth)}>
        <ModalCue>
          <Modal
            closeButtonText={getLabelFromStorage('general.close')}
            title={getLabelFromStorage('hub.become.member.title')}
            isOpen={showJoinClubModal}
            toggleModal={() => setJoinClubModalVisibility(false)}
          >
            <UpgradeToClub />
          </Modal>
        </ModalCue>

        <InboxModal />

        {isLarge !== isLargeInitialState && (
          <RenderComponent
            userIsFullMember={userIsFullMember}
            userIsPendingMember={userIsPendingMember}
            userIsNonMember={userIsNonMember}
            pendingMemberTryAgainAction={pendingMemberTryAgainAction}
            userDisplayName={userDisplayName}
            isUserLoading={isUserLoading}
            userEmail={userEmail}
            shouldShowClubCard={shouldShowClubCard}
          />
        )}
      </div>
      <DonateVoucherModal />
    </div>
  )
}

export default HubOverview
